import { SkeletonRows } from './SkeletonRows.js';

/**
 * Shared `_ops` block — the shape of a page, while the page's first request is in flight.
 *
 * Every list route used to answer `isPending` with a full-pane `Spinner` centred on
 * `bg-background`. Switching apps therefore emptied the pane to a flat colour with a
 * spinner in the middle of it — in the dark theme, a dark screen — and told the reader
 * nothing about what was arriving. This says what is arriving: a title, the action that
 * belongs beside it, the filter row, and the rows themselves.
 *
 * ONE region announces the wait. `SkeletonRows` drops its own `role="status"` when it is
 * given no label, so the rows inside this are decoration and the reader hears the wait once.
 *
 * It is deliberately one shape rather than a shape per screen. A skeleton is a promise about
 * layout, and a promise that is roughly right everywhere beats fifteen that each drift from
 * the screen they stand in for. Tune `rows` and `columns` to the table behind it.
 */
export function RouteSkeleton({
  label,
  rows = 8,
  columns = 4,
  /** Number of filter pills to stand in for. Pass 0 for a screen with no filter row. */
  filters = 4,
  /** Whether the page's header carries a primary action to the right of its title. */
  action = true,
}: {
  label: string;
  rows?: number;
  columns?: number;
  filters?: number;
  action?: boolean;
}) {
  return (
    <div
      role="status"
      aria-busy="true"
      aria-label={label}
      className="flex h-full flex-col gap-6 bg-background p-4 sm:p-6"
    >
      <div className="flex items-center justify-between gap-4">
        <div className="h-7 w-40 animate-pulse rounded-md bg-muted" />
        {action ? <div className="h-11 w-32 animate-pulse rounded-md bg-muted" /> : null}
      </div>
      {filters > 0 ? (
        <div className="flex flex-wrap gap-2">
          {Array.from({ length: filters }, (_, index) => (
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: positional shimmer placeholder
              key={index}
              className="h-9 w-24 animate-pulse rounded-md bg-muted"
            />
          ))}
        </div>
      ) : null}
      <div className="rounded-lg border border-border p-2">
        <SkeletonRows rows={rows} columns={columns} />
      </div>
    </div>
  );
}
